import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  adminCreateRedirection,
  adminDeleteRedirection,
  adminGetRedirections,
  adminUpdateRedirection,
} from './adminApi';

export interface IRedirection {
  _id: string;
  oldUrl: string;
  newUrl: string;
}

export interface IInitialRedirectionState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminRedirectionsListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IRedirection>;
  };
}

const initialState: IInitialRedirectionState = {
  status: 'idle',
  // Admin
  adminRedirectionsListData: undefined,
};

export const RedirectionsSlice = createSlice({
  initialState,
  name: 'redirections',
  reducers: {
    resetAdminRedirectionsListData: (state) => {
      state.adminRedirectionsListData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetRedirections.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminRedirectionsListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminRedirectionsListData = newAdminRedirectionsListData;
      })
      .addCase(adminCreateRedirection.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminRedirectionsListData) {
          const newAdminRedirectionsListData = {
            ...state.adminRedirectionsListData,
          };

          newAdminRedirectionsListData.list.unshift(result);

          if (newAdminRedirectionsListData.totalDocs) {
            newAdminRedirectionsListData.totalDocs += 1;
          } else {
            newAdminRedirectionsListData.totalDocs = 1;
          }

          state.adminRedirectionsListData = newAdminRedirectionsListData;
        }
      })
      .addCase(adminUpdateRedirection.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminRedirectionsListData &&
          state.adminRedirectionsListData.list &&
          state.adminRedirectionsListData.list.length
        ) {
          const newAdminRedirectionsListData = {
            ...state.adminRedirectionsListData,
          };

          const index = newAdminRedirectionsListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminRedirectionsListData.list[index] = result;
          }

          state.adminRedirectionsListData = newAdminRedirectionsListData;
        }
      })
      .addCase(adminDeleteRedirection.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminRedirectionsListData &&
          state.adminRedirectionsListData.list &&
          state.adminRedirectionsListData.list.length
        ) {
          const newAdminRedirectionsListData = {
            ...state.adminRedirectionsListData,
          };

          newAdminRedirectionsListData.totalDocs = newAdminRedirectionsListData.totalDocs
            ? newAdminRedirectionsListData.totalDocs - 1
            : 0;

          newAdminRedirectionsListData.list = newAdminRedirectionsListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminRedirectionsListData = newAdminRedirectionsListData;
        }
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateRedirection,
        adminGetRedirections,
        adminUpdateRedirection,
        adminDeleteRedirection
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateRedirection,
        adminGetRedirections,
        adminUpdateRedirection,
        adminDeleteRedirection
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateRedirection,
        adminGetRedirections,
        adminUpdateRedirection,
        adminDeleteRedirection
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getRedirectionsState = (
  state: RootState
): RootState['redirections'] => state.redirections;

export const { resetAdminRedirectionsListData } = RedirectionsSlice.actions;

export * from './adminApi';
export default RedirectionsSlice.reducer;
